import { useCallback } from 'react';

import { useRedirect, UseRedirectLinkType, UseRedirectQueryParams } from '@eneco/hooks/redirect';

import { useFlowBasketHelper } from './useFlowBasketHelper';
import { usePageContent } from './usePageContent';

interface Options {
  cleanBasket?: boolean;
  queryParams?: UseRedirectQueryParams;
}

const DEFAULT_OPTIONS: Options = { cleanBasket: true };

// `rootPath` is optional because it always could be: a page without a
// frontEndRootPath used to crash the destructure below rather than reach here,
// but every branch already tolerated the value being absent.
const adjustLink = (rootPath: string | undefined, link: UseRedirectLinkType) => {
  if (typeof link === 'string') {
    return link;
  }
  const { href, linktype } = link;
  if (linktype === 'external') {
    return link;
  }
  if (rootPath && href?.includes(rootPath)) {
    /**
     * Below is done because it seems there is no support for setting the link field's href values relatively on Sitecore.
     * Therefore the relative path is calculated.
     * Example:
     * frontendRootPath: energieproducten/warmtepomp/woningcheck/
     * link.href: energieproducten/warmtepomp/woningcheck/niet-geschikt/
     * result: /niet-geschikt/
     */
    let relativePath = href?.split?.(rootPath)?.[1];

    if (!relativePath.startsWith('/')) {
      relativePath = `/${relativePath}`;
    }
    return { ...link, href: relativePath };
  }
  /** If an internal link href is set as not relative somehow,
   * convert linktype to 'external' so that
   * it still gets redirected by setting window.location.href
   */
  return { ...link, linktype: 'external' };
};
export function useFlowRedirect() {
  const { frontEndRootPath: rootPath } = usePageContent();

  const { removeBasket } = useFlowBasketHelper();
  const redirect = useRedirect();
  const customRedirect = useCallback(
    (link?: UseRedirectLinkType, { cleanBasket = true, queryParams }: Options = DEFAULT_OPTIONS) => {
      if (link) {
        if (cleanBasket) {
          removeBasket();
        }

        const adjustedLink = adjustLink(rootPath, link);
        redirect({ link: adjustedLink, queryParams });
      }
    },
    [redirect, removeBasket, rootPath],
  );

  return { customRedirect };
}
